import React, {memo, useMemo} from 'react';
import Box from '@mui/material/Box';
import DisplayImage from '@/components/Ui-components/display-image';
import {borderRadius, colors, shadows} from '@/styles/tokens';
import {NARROW_LAYOUT_MAX, maxWidth} from '@/styles/breakpoints';

export interface LogoMarqueeItem {
    src: any;
    alt: string;
}

interface LogoMarqueeProps {
    items: LogoMarqueeItem[];
    /** Travel direction of the logos. */
    direction?: 'left' | 'right';
    /**
     * How fast the logos travel, in pixels per second. Lower = slower.
     *
     * Deliberately a speed and not a duration: the track is widened by repeating the
     * logo list to cover the viewport, so a fixed "seconds per pass" would make the
     * on-screen speed depend on how many logos the row happens to hold, and change
     * whenever one is added. Expressing it as px/s keeps the motion identical no
     * matter how the list grows.
     */
    speed?: number;
    /**
     * Cancels the row's 16px vertical padding out of the layout with an equal negative
     * margin. The padding itself stays, so the card shadows are still not clipped.
     */
    flush?: boolean;
}

/** Horizontal gap between cards — also the distance the track must overshoot to loop seamlessly. */
const GAP = '1.5rem';
const GAP_PX = 24;

/**
 * Per-item horizontal step at the `md` card width. Used only to decide how many times
 * to repeat the logo list; it is never applied as a layout value, so being a few pixels
 * off just means one redundant repeat, never a visible gap.
 */
const ITEM_ADVANCE_PX = 205 + GAP_PX; // CARD_WIDTH + gap

/**
 * Widest viewport a single track must cover on its own. A track narrower than the
 * viewport would leave the far edge empty at the loop seam, so this is sized for
 * ultrawide desktops rather than the common 1440.
 */
const MAX_VIEWPORT_PX = 2560;

/**
 * How many times the logo list must repeat inside one track for the track's content to
 * span {@link MAX_VIEWPORT_PX}. A long list needs no repetition and returns 1.
 */
const trackRepeats = (count: number) => (count > 0 ? Math.max(1, Math.ceil(MAX_VIEWPORT_PX / (count * ITEM_ADVANCE_PX))) : 1);

/**
 * Infinite, seamless logo marquee.
 *
 * ## How the seamless loop works
 *
 * Two identical tracks sit side by side and slide together by exactly one track's
 * width plus one gap. At the moment the animation resets, the second track's logos
 * occupy precisely where the first track's were, so there is no visible jump — and
 * no JS measuring anything.
 *
 * That identity is the whole trick, and it is why a track must never be stretched
 * wider than its own content: the animation travels the track's *box* width, so any
 * slack inside the box shows up as a blank gap at every seam. An earlier
 * `min-width: 100%` did exactly that on any row whose logos were narrower than the
 * viewport. Instead the logo list is repeated inside each track until the track's
 * content alone spans the widest viewport we support — content-sized, no slack.
 *
 * Exactly one pass over the logo list — the first pass of the first track — is
 * accessible. Every other copy, whether a repeat inside a track or the whole second
 * track, is marked decorative: `aria-hidden` plus an empty `alt`. So each client name
 * appears once in the accessibility tree and once in the HTML, however many visual
 * copies the layout needs. On a client-proof section that matters; duplicated brand
 * names would otherwise read as keyword repetition.
 *
 * ## Why CSS and not GSAP
 *
 * A `transform` keyframe animation runs on the compositor, so the marquee costs no
 * main-thread work and no scroll listener — it cannot regress INP. Where the section
 * sits inside a {@link CvSection} whose `content-visibility: auto` is active, the
 * browser also stops animating it while off-screen at no cost to us.
 *
 * Motion is suppressed entirely under `prefers-reduced-motion: reduce`; the row
 * then becomes a normal horizontally-scrollable strip, so no logo is unreachable.
 */
const marqueeViewportSx = {
    display: 'flex',
    gap: GAP,
    overflow: 'hidden',
    // `overflow: hidden` is required to clip the loop horizontally, but it clips
    // vertically too — and it clips at the padding edge. Without this padding the
    // cards' own drop shadow was being sliced off flat along the row's top and
    // bottom. Sized off shadows.card ('0 4px 12px'), which reaches 8px above a card
    // and 16px below it; 16px each side covers both and keeps the row symmetrical.
    py: 2,
    userSelect: 'none',
    // Fades the logos into the page at both edges instead of clipping them on a hard
    // line. -webkit- prefix kept for Safari, which still needs it for mask-image.
    maskImage: 'linear-gradient(to right, transparent, #000 6%, #000 94%, transparent)',
    WebkitMaskImage: 'linear-gradient(to right, transparent, #000 6%, #000 94%, transparent)',
    // Hovering anywhere on the row holds it still so a logo can actually be looked at.
    '&:hover > *': {animationPlayState: 'paused'},
    '@media (prefers-reduced-motion: reduce)': {
        overflowX: 'auto',
        '& > *': {animation: 'none'}
    }
} as const;

const flushViewportSx = {...marqueeViewportSx, my: -2} as const;

const trackSx = {
    display: 'flex',
    alignItems: 'center',
    gap: GAP,
    flexShrink: 0,
    animation: `logoMarqueeScroll var(--marquee-duration) linear infinite`,
    animationDirection: 'var(--marquee-direction)' as any,
    '@keyframes logoMarqueeScroll': {
        from: {transform: 'translateX(0)'},
        to: {transform: `translateX(calc(-100% - ${GAP}))`}
    }
} as const;

const CARD_WIDTH = 205;
const CARD_WIDTH_COMPACT = 170;
/** Card heights, exported so ClientServed can reserve the rows' height before they mount. */
export const CARD_HEIGHT = 100;
export const CARD_HEIGHT_COMPACT = 84;
const LOGO_MAX_WIDTH = 150;
const LOGO_MAX_WIDTH_NARROW = 115;

const cardSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: {xs: CARD_WIDTH_COMPACT, md: CARD_WIDTH},
    height: {xs: CARD_HEIGHT_COMPACT, md: CARD_HEIGHT},
    px: 2,
    bgcolor: colors.background.default,
    borderRadius: borderRadius.md,
    boxShadow: shadows.card,
    // Laptop-class screens take the phone card size and a smaller logo. The logo's cap
    // lives here rather than in its inline style so it can step down with the card.
    [maxWidth(NARROW_LAYOUT_MAX)]: {width: CARD_WIDTH_COMPACT, height: CARD_HEIGHT_COMPACT},
    '& img': {maxWidth: LOGO_MAX_WIDTH, [maxWidth(NARROW_LAYOUT_MAX)]: {maxWidth: LOGO_MAX_WIDTH_NARROW}}
} as const;

const logoStyle = {width: '100%', height: 'auto', objectFit: 'contain' as const};

const LogoMarquee = ({items, direction = 'left', speed = 26, flush = false}: LogoMarqueeProps) => {
    const trackItems = useMemo(() => {
        const repeats = trackRepeats(items.length);
        return repeats === 1 ? items : Array.from({length: repeats}, () => items).flat();
    }, [items]);

    // One pass covers the track's width, so time = distance / speed. Approximate width
    // is fine here for the same reason as the repeat count: a few pixels out shifts the
    // speed imperceptibly, it cannot break the seam.
    const duration = (trackItems.length * ITEM_ADVANCE_PX) / speed;

    return (
        <Box
            sx={flush ? flushViewportSx : marqueeViewportSx}
            style={
                {
                    '--marquee-duration': `${duration}s`,
                    // Reversing a seamless loop is still seamless — same distance, same
                    // reset point — so one keyframe set covers both directions.
                    '--marquee-direction': direction === 'right' ? 'reverse' : 'normal'
                } as React.CSSProperties
            }
        >
            {[0, 1].map((track) => (
                <Box key={track} sx={trackSx}>
                    {trackItems.map((item, index) => {
                        // Only the first track's first pass carries the real alt text.
                        const decorative = track !== 0 || index >= items.length;
                        return (
                            <Box key={`${track}-${index}`} sx={cardSx} aria-hidden={decorative || undefined}>
                                <DisplayImage src={item.src} alt={decorative ? '' : item.alt} priority={false} style={logoStyle} />
                            </Box>
                        );
                    })}
                </Box>
            ))}
        </Box>
    );
};

export default memo(LogoMarquee);
